import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Divider,
  Stack,
  TextField,
  MenuItem,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";

import ProjectCard from "../components/ProjectCard";
import api from "../api/axios";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";

import DrillingTemplate from "../components/projectTemplates/DrillingTemplate";
import OnceJobTemplate from "../components/projectTemplates/OnceJobTemplate";

dayjs.extend(duration);
dayjs.extend(relativeTime);

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTemplate, setProjectTemplate] = useState(null);
  const [progressData, setProgressData] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      const { data: response } = await api.get("/departments/list");
      setDepartments(response.data);
    };

    const fetchProjects = async () => {
      try {
        const { data: response } = await api.get("/projects/active_list");
        setProjects(response.data);
      } catch (err) {
        console.error("Error fetching projects:", err);
      }
    };

    fetchDepartments();
    fetchProjects();
  }, []);

  const handleProjectSelect = async (project) => {
    if (loading) return;

    setSelectedProject(null);
    setProjectTemplate(null);
    setProgressData(null);

    try {
      setLoading(true);

      const { data: response } = await api.get(`/projects/view/${project.id}`);

      setSelectedProject(response.data);

      const template =
        departments.find((dept) => dept.id === response.data.dept_id)
          ?.default_template || null;

      setProjectTemplate(template);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching project details:", err);
      setLoading(false);
    }
  };

  const getDaysDiff = (date, deadline = false) => {
    if (!date) return "-";

    const target = dayjs(date);
    const today = dayjs();

    const diff = target.diff(today, "days");

    const daysCount = Math.abs(diff);

    const message =
      diff >= 0
        ? `${daysCount} days remaining`
        : `${daysCount} days ${deadline ? "overdue" : "passed"}`;

    return message;
  };

  /* ================= FILTERED PROJECTS ================= */

  const filteredProjects = selectedDepartment
    ? projects.filter((p) => String(p.dept_id) === String(selectedDepartment))
    : projects;

  return (
    <Box p={1}>
      {/* HEADER + FILTER */}
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={600}>
          Ongoing Projects
        </Typography>

        <TextField
          select
          size="small"
          label="Department Filter"
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          sx={{
            minWidth: 220,
            background: "rgba(255,255,255,0.04)",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        >
          <MenuItem value="">All Departments</MenuItem>

          {departments.map((dept) => (
            <MenuItem key={dept.id} value={dept.id}>
              {dept.dept_name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Grid container spacing={2}>
        {/* ================= LEFT PANEL ================= */}
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
          <Grid
            container
            sx={{
              overflowY: "auto",
              maxHeight: "80vh",
            }}
            pr="5px"
          >
            {filteredProjects.map((project) => (
              <Grid
                item
                size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}
                key={project.id}
                p={1}
              >
                <ProjectCard project={project} onClick={handleProjectSelect} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* ================= RIGHT PANEL ================= */}
        <Grid
          size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}
          sx={{
            borderLeft: "1px solid rgba(255,255,255,0.12)",
            overflowY: "auto",
            overflowX: "hidden",
            maxHeight: "80vh",
          }}
        >
          <Grid
            container
            sx={{
              flexDirection: "column",
              rowGap: 2,
              p: 1,
              borderRadius: 2,
            }}
          >
            {/* NO PROJECT SELECTED */}
            {!selectedProject && (
              <Typography variant="body1" color="textSecondary">
                Tap on a project to view details.
              </Typography>
            )}

            {/* ================= PROJECT INFO ================= */}
            {selectedProject && (
              <Box
                sx={{
                  p: 1,
                  borderRadius: 3,
                  position: "relative",

                  background: "rgba(255, 255, 255, 0.06)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",

                  border: "1px solid rgba(255,255,255,0.12)",

                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",

                  transition: "all 0.2s ease-in-out",

                  "&:active": {
                    boxShadow:
                      "0 0 0 2px rgba(34,197,94,0.6), 0 0 18px rgba(34,197,94,0.7)",
                    transform: "scale(0.98)",
                  },

                  mx: "auto",
                }}
              >
                {/* TIME BADGE */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    background: "rgba(0,0,0,0.35)",
                    backdropFilter: "blur(6px)",
                  }}
                  title="Time since project allotment"
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: 16,
                      color: "primary.light",
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 500,
                    }}
                  >
                    {selectedProject.deadline
                      ? getDaysDiff(selectedProject.deadline, true)
                      : getDaysDiff(
                          selectedProject.workorder_date ??
                            selectedProject.created_at,
                        )}
                  </Typography>
                </Box>

                {/* TITLE */}
                <Typography variant="h6" fontWeight={700}>
                  {selectedProject.project_name}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    mb: 2,
                  }}
                >
                  {selectedProject.customer_name}
                </Typography>

                {/* INFO GRID */}
                <Grid container spacing={2}>
                  {/* CUSTOMER POC */}
                  <Grid item xs={6} sm={4}>
                    <Stack spacing={0.5} textAlign="center">
                      <Typography variant="subtitle2" color="primary.light">
                        {selectedProject.customer_poc_name || "-"}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="rgba(255,255,255,0.6)"
                      >
                        Customer POC
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* QTY */}
                  <Grid item xs={6} sm={4}>
                    <Stack spacing={0.5} textAlign="center">
                      <Typography variant="subtitle2" color="primary.light">
                        {selectedProject.qty ?? "-"}{" "}
                        {selectedProject.unit ?? ""}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="rgba(255,255,255,0.6)"
                      >
                        Awarded Job Qty.
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* BD PERSON */}
                  <Grid item xs={6} sm={4}>
                    <Stack spacing={0.5} textAlign="center">
                      <Typography variant="subtitle2" color="primary.light">
                        {selectedProject.bd_name || "-"}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="rgba(255,255,255,0.6)"
                      >
                        SPPL BD Person
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* WO DATE */}
                  <Grid item xs={6} sm={4}>
                    <Stack spacing={0.5} textAlign="center">
                      <Typography variant="subtitle2" color="primary.light">
                        {selectedProject.workorder_date
                          ? dayjs(selectedProject.workorder_date).format(
                              "DD-MM-YYYY",
                            )
                          : "-"}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="rgba(255,255,255,0.6)"
                      >
                        Work Order Date
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* DEADLINE */}
                  <Grid item xs={6} sm={4}>
                    <Stack spacing={0.5} textAlign="center">
                      <Typography variant="subtitle2" color="primary.light">
                        {selectedProject.deadline
                          ? dayjs(selectedProject.deadline).format("DD-MM-YYYY")
                          : "-"}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="rgba(255,255,255,0.6)"
                      >
                        Contract Validity
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ================= PAYMENT WARNING ================= */}
            {selectedProject &&
              selectedProject.payment_terms_verified === 0 && (
                <Box mt={2} p={2} bgcolor="rgba(255,0,0,0.1)" borderRadius={2}>
                  <Typography variant="body1" color="error">
                    Payment Terms Verification Pending, Contact Finance
                    Department.
                  </Typography>
                </Box>
              )}

            {/* ================= TEMPLATE SECTION ================= */}
            {selectedProject &&
              selectedProject.payment_terms_verified === 1 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1,
                    bgcolor: "rgba(0,255,0,0.1)",
                    borderRadius: 2,
                  }}
                >
                  {projectTemplate === "drilling" && selectedProject && (
                    <DrillingTemplate selectedProject={selectedProject} />
                  )}

                  {["once_office", "once_field_once_office"].includes(
                    projectTemplate,
                  ) &&
                    selectedProject && (
                      <OnceJobTemplate selectedProject={selectedProject} />
                    )}
                </Box>
              )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
